import AccountTreeIcon from "@mui/icons-material/AccountTree";
import AutoGraphIcon from "@mui/icons-material/AutoGraph";
import BarChartIcon from "@mui/icons-material/BarChart";
import CandlestickChartIcon from "@mui/icons-material/CandlestickChart";
import InsightsIcon from "@mui/icons-material/Insights";
import PaidIcon from "@mui/icons-material/Paid";
import PieChartIcon from "@mui/icons-material/PieChart";
import StackedBarChartIcon from "@mui/icons-material/StackedBarChart";
import TimelineIcon from "@mui/icons-material/Timeline";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import copy from "copy-to-clipboard";
import { useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import { useQuery, useQueryClient } from "react-query";
import { useNavigate } from "react-router-dom";
import Loader from "../Shared/Loader";
import { apiConnectorGet, apiConnectorPost } from "../utils/APIConnector";
import { endpoint, frontend } from "../utils/APIRoutes";
import { getFloatingValue } from "../utils/utilityFun";
import tether from "../images/tether.png";
import Diversity3Icon from "@mui/icons-material/Diversity3";
import CurrencyExchangeIcon from "@mui/icons-material/CurrencyExchange";
import AnalogClock from "./pages/AnalogClock";
import StaticSpeedometer from "./pages/StaticSpeedometer";
import DirectBusinessSpeedometer from "./pages/DirectBusinessSpeedometer";
import DirectBusinessGauge from "./pages/DirectBusinessGauge";

function GearIcon({
  size = 80,
  clockwise = true,
  speed = 4,
  color = "#2563EB",
  teeth = 8,
  active = true,
}) {
  const animStyle = {
    animation: active
      ? `${clockwise ? "profitSpinCW" : "profitSpinCCW"} ${speed}s linear infinite`
      : "none",
    transformOrigin: "center",
  };

  const radius = size / 2;
  const innerRadius = radius * 0.38;
  const toothHeight = radius * 0.22;
  const centerX = radius;
  const centerY = radius;

  let pathData = "";
  for (let index = 0; index < teeth; index += 1) {
    const angle1 = (index / teeth) * 2 * Math.PI - Math.PI / 2;
    const angle2 = ((index + 0.3) / teeth) * 2 * Math.PI - Math.PI / 2;
    const angle3 = ((index + 0.7) / teeth) * 2 * Math.PI - Math.PI / 2;
    const angle4 = ((index + 1) / teeth) * 2 * Math.PI - Math.PI / 2;

    const x1 = centerX + (radius - 2) * Math.cos(angle1);
    const y1 = centerY + (radius - 2) * Math.sin(angle1);
    const x2 = centerX + (radius + toothHeight) * Math.cos(angle2);
    const y2 = centerY + (radius + toothHeight) * Math.sin(angle2);
    const x3 = centerX + (radius + toothHeight) * Math.cos(angle3);
    const y3 = centerY + (radius + toothHeight) * Math.sin(angle3);
    const x4 = centerX + (radius - 2) * Math.cos(angle4);
    const y4 = centerY + (radius - 2) * Math.sin(angle4);

    if (index === 0) pathData += `M ${x1} ${y1} `;
    pathData += `L ${x2} ${y2} L ${x3} ${y3} L ${x4} ${y4} `;
  }
  pathData += "Z";

  const uid = clockwise ? "profit-cw" : "profit-ccw";

  return (
    <svg
      width={size + toothHeight * 2}
      height={size + toothHeight * 2}
      viewBox={`${-toothHeight} ${-toothHeight} ${size + toothHeight * 2} ${size + toothHeight * 2}`}
      style={animStyle}
    >
      <defs>
        <radialGradient id={`gear_${uid}`} cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor={color} stopOpacity="0.22" />
          <stop offset="100%" stopColor={color} stopOpacity="0.04" />
        </radialGradient>
        <filter id={`glow_${uid}`}>
          <feGaussianBlur stdDeviation="2.5" result="coloredBlur" />
          <feMerge>
            <feMergeNode in="coloredBlur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>
      <path
        d={pathData}
        fill={`url(#gear_${uid})`}
        stroke={color}
        strokeWidth="1.4"
        filter={`url(#glow_${uid})`}
      />
      <circle
        cx={centerX}
        cy={centerY}
        r={innerRadius}
        fill="none"
        stroke={color}
        strokeWidth="1.1"
        strokeDasharray="4 3"
        opacity="0.6"
      />
      <circle
        cx={centerX}
        cy={centerY}
        r={radius * 0.12}
        fill={color}
        opacity="0.9"
      />
    </svg>
  );
}

function ProfitGearWidget({
  isActive,
  isProfitActive,
  mt5Accounts,
  onOpenAccount,
}) {
  const audioRef = useRef(null);
  const [isMuted, setIsMuted] = useState(false);

  const getIstSecondsRemaining = () => {
    const now = new Date();
    const istOffset = 5.5 * 60 * 60 * 1000;
    const istNow = new Date(now.getTime() + istOffset);
    const elapsed =
      istNow.getUTCHours() * 3600 +
      istNow.getUTCMinutes() * 60 +
      istNow.getUTCSeconds();

    return 86400 - elapsed;
  };

  const [remaining, setRemaining] = useState(getIstSecondsRemaining());

  useEffect(() => {
    const timerId = setInterval(
      () => setRemaining(getIstSecondsRemaining()),
      1000,
    );
    return () => clearInterval(timerId);
  }, []);

  useEffect(() => {
    const audioEl = audioRef.current;
    if (!audioEl) return;

    audioEl.loop = true;
    audioEl.volume = 0.35;

    if (!isActive || isMuted) {
      audioEl?.pause();
      return;
    }

    const playPromise = audioEl.play();
    if (playPromise && typeof playPromise.catch === "function") {
      playPromise.catch(() => {
        // Autoplay may be blocked until the user interacts with the page.
      });
    }
  }, [isActive, isMuted]);

  useEffect(() => {
    return () => {
      const audioEl = audioRef.current;
      if (audioEl || !isProfitActive) audioEl?.pause();
    };
  }, [isProfitActive]);

  const hours = String(Math.floor(remaining / 3600)).padStart(2, "0");
  const minutes = String(Math.floor((remaining % 3600) / 60)).padStart(2, "0");
  const seconds = String(remaining % 60).padStart(2, "0");

  const buttonThemes = [
    {
      border:
        "conic-gradient(from var(--angle,0deg),#ffb347,#ff7b00,#ff3d54,#ff0080,#ffb347,#ff7b00,#ff3d54,#ffb347)",
      glow: "conic-gradient(from var(--angle,0deg),transparent 70%,rgba(255,100,50,0.45) 80%,rgba(255,180,50,0.45) 90%,transparent 100%)",
      inner: "bg-gradient-to-br from-[#1a0a00] via-[#2a0d10] to-[#1a0800]",
      shimmer:
        "linear-gradient(105deg,transparent 30%,rgba(255,200,100,0.18) 50%,transparent 70%)",
      text: "linear-gradient(135deg,#ffe0b0,#ffb347,#ff7b00)",
      textGlow: "drop-shadow(0 0 6px rgba(255,140,60,0.5))",
    },
    {
      border:
        "conic-gradient(from var(--angle,0deg),#8a6dff,#5b8cff,#3ec7ff,#8a6dff,#5b8cff,#3ec7ff,#8a6dff)",
      glow: "conic-gradient(from var(--angle,0deg),transparent 70%,rgba(91,140,255,0.45) 80%,rgba(62,199,255,0.45) 90%,transparent 100%)",
      inner: "bg-gradient-to-br from-[#11122a] via-[#0f1a30] to-[#0a1830]",
      shimmer:
        "linear-gradient(105deg,transparent 30%,rgba(120,180,255,0.18) 50%,transparent 70%)",
      text: "linear-gradient(135deg,#c8dcff,#7bb2ff,#3ec7ff)",
      textGlow: "drop-shadow(0 0 6px rgba(91,140,255,0.5))",
    },
    {
      border:
        "conic-gradient(from var(--angle,0deg),#2563EB,#22D3EE,#1D4ED8,#2563EB,#22D3EE,#1D4ED8,#2563EB)",
      glow: "conic-gradient(from var(--angle,0deg),transparent 70%,rgba(0,180,190,0.45) 80%,rgba(33,193,92,0.45) 90%,transparent 100%)",
      inner: "bg-gradient-to-br from-[#061a1f] via-[#0a1f1e] to-[#061a12]",
      shimmer:
        "linear-gradient(105deg,transparent 30%,rgba(89,225,232,0.18) 50%,transparent 70%)",
      text: "linear-gradient(135deg,#22D3EE,#2563EB,#1D4ED8)",
      textGlow: "drop-shadow(0 0 6px rgba(0,180,190,0.5))",
    },
    {
      border:
        "conic-gradient(from var(--angle,0deg),#ffe082,#ffca28,#f59e0b,#ffe082,#ffca28,#f59e0b,#ffe082)",
      glow: "conic-gradient(from var(--angle,0deg),transparent 70%,rgba(255,202,40,0.45) 80%,rgba(245,158,11,0.45) 90%,transparent 100%)",
      inner: "bg-gradient-to-br from-[#1f1806] via-[#221a08] to-[#191103]",
      shimmer:
        "linear-gradient(105deg,transparent 30%,rgba(255,224,130,0.18) 50%,transparent 70%)",
      text: "linear-gradient(135deg,#fff3bf,#ffca28,#f59e0b)",
      textGlow: "drop-shadow(0 0 6px rgba(255,202,40,0.5))",
    },
  ];

  return (
    <div className="h-full rounded-2xl border border-[#2563EB]/30 bg-gradient-to-br from-[#060E1C] via-[#080F1C] to-[#0B1E36] px-5 py-4 shadow-xl shadow-[#2563EB]/10 relative overflow-hidden">
      <audio ref={audioRef} src="/gearsound.mp3" preload="auto" />
      <style>{`
        @keyframes profitSpinCW { to { transform: rotate(360deg); } }
        @keyframes profitSpinCCW { to { transform: rotate(-360deg); } }
      `}</style>

      <button
        type="button"
        onClick={() => setIsMuted((prev) => !prev)}
        className="absolute right-3 top-3 z-20 inline-flex items-center gap-2 rounded-full border border-[#2563EB]/25 bg-[#071526]/90 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.2em] text-[#22D3EE] transition-all duration-200 hover:border-[#1D4ED8]/45 hover:text-[#1D4ED8]"
      >
        <svg
          className="h-3.5 w-3.5"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          {isMuted ? (
            <>
              <path d="M11 5L6 9H3v6h3l5 4V5z" />
              <path d="M23 9l-6 6" />
              <path d="M17 9l6 6" />
            </>
          ) : (
            <>
              <path d="M11 5L6 9H3v6h3l5 4V5z" />
              <path d="M15.54 8.46a5 5 0 010 7.07" />
              <path d="M19.07 4.93a10 10 0 010 14.14" />
            </>
          )}
        </svg>
        <span>{isMuted ? "Unmute" : "Mute"}</span>
      </button>

      <div className="absolute top-0 right-0 w-40 h-40 bg-[#2563EB]/5 rounded-full blur-3xl"></div>
      <div className="absolute -bottom-10 -left-10 w-32 h-32 bg-[#1D4ED8]/5 rounded-full blur-2xl"></div>
      <div className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-[#22D3EE] via-[#2563EB] to-[#1D4ED8]"></div>

      <div className="relative z-10 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <div
              className={`w-2 h-2 rounded-full ${isActive ? "bg-[#2563EB] animate-pulse" : "bg-white/20"}`}
            ></div>
            <p className="text-gray-400 text-xs font-medium tracking-[0.24em] uppercase">
              Profit Cycle
            </p>
          </div>
          <h3 className="text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-[#22D3EE] via-[#2563EB] to-[#1D4ED8] mb-1">
            {isActive ? "Auto Profit Active" : "Profit Inactive"}
          </h3>
          <p className="text-[11px] text-gray-500 uppercase tracking-[0.18em]">
            Reset timer in IST
          </p>
          <div className="mt-3 flex flex-col gap-2">
            {/* Buttons Row */}
            <div className="flex flex-wrap items-stretch gap-2">
              {mt5Accounts?.length ? (
                mt5Accounts.map((account, index) => {
                  const theme = buttonThemes[index % buttonThemes.length];
                  return (
                    <button
                      key={
                        account?.m07_ac_id || `${account?.m07_ac_no}-${index}`
                      }
                      type="button"
                      onClick={() => onOpenAccount(account?.m07_ac_no)}
                      className="group relative inline-flex h-11 items-center justify-center overflow-visible rounded-xl px-4 text-[11px] font-black uppercase tracking-[0.18em] text-white outline-none transition-transform duration-300 hover:scale-[1.04] flex-1 min-w-[130px]"
                      style={{ background: "transparent", border: "none" }}
                    >
                      <span
                        className="pointer-events-none absolute -inset-0.5 rounded-[14px] -z-10"
                        style={{
                          background: theme.border,
                          animation: "spin-border 2.5s linear infinite",
                        }}
                      />
                      <span
                        className="pointer-events-none absolute -inset-1 rounded-[15px] -z-20"
                        style={{
                          background: theme.glow,
                          filter: "blur(6px)",
                          animation: "spin-border 2.5s linear infinite",
                        }}
                      />
                      <span
                        className={`absolute inset-[1.5px] rounded-[11px] -z-10 ${theme.inner}`}
                      />
                      <span
                        className="pointer-events-none absolute inset-[1.5px] rounded-[11px] z-0"
                        style={{
                          background: theme.shimmer,
                          backgroundSize: "200% 100%",
                          animation: "shimmer-slide 2.2s ease-in-out infinite",
                        }}
                      />
                      <span
                        className="relative z-10 font-black"
                        style={{
                          background: theme.text,
                          WebkitBackgroundClip: "text",
                          WebkitTextFillColor: "transparent",
                          filter: theme.textGlow,
                        }}
                      >
                        MT5- {account?.m07_ac_no}
                      </span>
                    </button>
                  );
                })
              ) : (
                <div className="inline-flex h-11 items-center rounded-lg border border-[#2563EB]/20 bg-[#2563EB]/5 px-3">
                  <span className="text-[11px] font-bold tracking-[0.08em] text-[#22D3EE]">
                    No Active MT5 Account
                  </span>
                </div>
              )}
            </div>
            {/* Timer Row */}
            <div className="inline-flex h-11 items-center rounded-lg border border-[#2563EB]/20 bg-[#2563EB]/5 px-3 self-start">
              <span className="text-[18px] font-bold tracking-[0.18em] text-[#22D3EE]">
                {hours}:{minutes}:{seconds}
              </span>
            </div>
          </div>
        </div>

        <div className="relative h-[110px] lg:w-[220px]">
          <div
            className={`absolute left-1/2 top-1/2 -translate-x-[105px] -translate-y-1/2 transition-opacity duration-300 ${isActive ? "opacity-95" : "opacity-40"}`}
          >
            <GearIcon
              size={84}
              clockwise={true}
              speed={3.4}
              color="#2563EB"
              teeth={10}
              active={isActive}
            />
          </div>
          <div
            className={`absolute left-1/2 top-1/2 translate-x-[10px] -translate-y-1/2 transition-opacity duration-300 ${isActive ? "opacity-90" : "opacity-35"}`}
          >
            <GearIcon
              size={58}
              clockwise={false}
              speed={2.2}
              color="#1D4ED8"
              teeth={7}
              active={isActive}
            />
          </div>
          <div className="absolute bottom-0 left-0 right-0 text-center text-[10px] uppercase tracking-[0.25em] text-white/30">
            {isActive ? "Profit Processing" : "Waiting For Activation"}
          </div>
        </div>
      </div>
    </div>
  );
}

const Card = ({ title, value, color, icon = "dollar", path, navigate }) => {
  const colors = {
    cyan: {
      gradient: "from-[#060E1C] via-[#080F1C] to-[#0B1E36]",
      border: "border-[#2563EB]/30",
      accent: "from-[#22D3EE] via-[#2563EB] to-transparent",
      text: "from-[#22D3EE] via-[#2563EB] to-[#1D4ED8]",
      glow: "bg-[#2563EB]/5 group-hover:bg-[#2563EB]/10",
      glowSmall: "bg-[#1D4ED8]/5",
      shadow: "shadow-[#2563EB]/10 hover:shadow-[#2563EB]/20",
      progressBar: "from-[#2563EB] to-[#1D4ED8]",
      progressShadow: "shadow-[#2563EB]/50",
      iconColor: "text-[#2563EB]",
      iconBg: "bg-[#2563EB]/10",
      iconBorder: "border-[#2563EB]/30",
      iconGlow: "bg-[#2563EB]/20",
      pulseColor: "bg-[#2563EB]",
      orbiting: "bg-[#2563EB]",
      shine: "via-[#2563EB]/5",
    },
    yellow: {
      gradient: "from-[#060E1C] via-[#080F1C] to-[#0B1E36]",
      border: "border-[#1D4ED8]/30",
      accent: "from-[#2563EB] via-[#1D4ED8] to-transparent",
      text: "from-[#22D3EE] via-[#2563EB] to-[#1D4ED8]",
      glow: "bg-[#2563EB]/5 group-hover:bg-[#2563EB]/10",
      glowSmall: "bg-[#1D4ED8]/5",
      shadow: "shadow-[#2563EB]/10 hover:shadow-[#1D4ED8]/20",
      progressBar: "from-[#2563EB] to-[#1D4ED8]",
      progressShadow: "shadow-[#2563EB]/50",
      iconColor: "text-[#1D4ED8]",
      iconBg: "bg-[#2563EB]/10",
      iconBorder: "border-[#1D4ED8]/30",
      iconGlow: "bg-[#2563EB]/20",
      pulseColor: "bg-[#1D4ED8]",
      orbiting: "bg-[#1D4ED8]",
      shine: "via-[#2563EB]/5",
    },
    green: {
      gradient: "from-[#060E1C] via-[#080F1C] to-[#0B1E36]",
      border: "border-[#1D4ED8]/30",
      accent: "from-[#22D3EE] via-[#2563EB] to-transparent",
      text: "from-[#22D3EE] via-[#2563EB] to-[#1D4ED8]",
      glow: "bg-[#2563EB]/5 group-hover:bg-[#2563EB]/10",
      glowSmall: "bg-[#1D4ED8]/5",
      shadow: "shadow-[#2563EB]/10 hover:shadow-[#1D4ED8]/20",
      progressBar: "from-[#2563EB] to-[#1D4ED8]",
      progressShadow: "shadow-[#2563EB]/50",
      iconColor: "text-[#2563EB]",
      iconBg: "bg-[#2563EB]/10",
      iconBorder: "border-[#1D4ED8]/30",
      iconGlow: "bg-[#2563EB]/20",
      pulseColor: "bg-[#2563EB]",
      orbiting: "bg-[#1D4ED8]",
      shine: "via-[#2563EB]/5",
    },
    red: {
      gradient: "from-[#060E1C] via-[#080F1C] to-[#0B1E36]",
      border: "border-red-400/30",
      accent: "from-red-400 via-rose-500 to-transparent",
      text: "from-red-300 via-red-400 to-rose-500",
      glow: "bg-red-400/5 group-hover:bg-red-400/10",
      glowSmall: "bg-rose-500/5",
      shadow: "shadow-red-400/10 hover:shadow-red-400/20",
      progressBar: "from-red-500 to-red-400",
      progressShadow: "shadow-red-400/50",
      iconColor: "text-red-400",
      iconBg: "bg-red-400/10",
      iconBorder: "border-red-400/30",
      iconGlow: "bg-red-400/20",
      pulseColor: "bg-red-400",
      orbiting: "bg-red-400",
      shine: "via-red-400/5",
    },
  };

  const currentColor = colors[color];
  const chart = [
    <BarChartIcon color={currentColor.iconColor} />,
    <PieChartIcon color={currentColor.iconColor} />,
    <TimelineIcon color={currentColor.iconColor} />,
    <InsightsIcon color={currentColor.iconColor} />,
    <TrendingUpIcon color={currentColor.iconColor} />,
    <CandlestickChartIcon color={currentColor.iconColor} />,
    <StackedBarChartIcon color={currentColor.iconColor} />,
    <AutoGraphIcon color={currentColor.iconColor} />,
  ];

  return (
    <div
      onClick={() => path && navigate(path)}
      className={`bg-gradient-to-br ${currentColor.gradient} rounded-2xl p-4 sm:p-6 border ${currentColor.border} shadow-xl ${currentColor.shadow} relative overflow-hidden group transition-all duration-300 hover:-translate-y-1`}
    >
      {/* Animated background effects */}
      <div
        className={`absolute top-0 right-0 w-40 h-40 ${currentColor.glow} rounded-full blur-3xl transition-all duration-500`}
      ></div>
      <div
        className={`absolute -bottom-10 -left-10 w-32 h-32 ${currentColor.glowSmall} rounded-full blur-2xl`}
      ></div>

      {/* Accent line */}
      <div
        className={`absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b ${currentColor.accent}`}
      ></div>

      {/* Decorative corner elements */}
      <div
        className={`absolute top-0 right-0 w-20 h-20 border-t-2 border-r-2 ${currentColor.border} rounded-tr-2xl opacity-50`}
      ></div>
      <div
        className={`absolute bottom-0 left-0 w-16 h-16 border-b-2 border-l-2 ${currentColor.border} rounded-bl-2xl opacity-30`}
      ></div>

      {/* Content */}
      <div className="relative z-10">
        <div className="flex items-center gap-2 mb-2">
          <div
            className={`w-2 h-2 rounded-full ${currentColor.pulseColor} animate-pulse`}
          ></div>
          <p className="text-gray-400 text-sm font-medium tracking-wide uppercase">
            {title}
          </p>
        </div>

        <h2 className="text-3xl font-bold mb-1">
          <span
            className={`text-transparent bg-clip-text bg-gradient-to-r ${currentColor.text}`}
          >
            {value}
          </span>
        </h2>
      </div>

      {/* Icon Area */}
      <div className="absolute top-4 right-4 w-16 h-16 flex items-center justify-center">
        <div className="relative">
          {/* Glow effect behind icon */}
          <div
            className={`absolute inset-0 ${currentColor.iconGlow} rounded-full blur-xl animate-pulse`}
          ></div>

          {/* Icon container with gradient border */}
          <div
            className={`relative w-16 h-16 rounded-full bg-gradient-to-br ${currentColor.iconBg} to-transparent p-0.5`}
          >
            <div className="w-full h-full rounded-full bg-[#071526] flex items-center justify-center backdrop-blur-sm">
              <div
                className={`${currentColor.iconColor} transform group-hover:scale-110 transition-transform duration-300 text-xl`}
              >
                {icon === "user" ? (
                  <AccountTreeIcon />
                ) : (
                  chart[Math.floor(Math.random() * chart.length)] || (
                    <PaidIcon />
                  )
                )}
              </div>
            </div>
          </div>

          {/* Orbiting dot */}
          <div className="absolute top-1/2 left-1/2 w-20 h-20 -translate-x-1/2 -translate-y-1/2">
            <div
              className={`absolute top-0 left-1/2 w-1.5 h-1.5 ${currentColor.orbiting} rounded-full -translate-x-1/2 animate-ping`}
            ></div>
          </div>
        </div>
      </div>

      {/* Shine effect on hover */}
      <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
        <div
          className={`absolute inset-0 bg-gradient-to-r from-transparent ${currentColor.shine} to-transparent -skew-x-12 translate-x-[-200%] group-hover:translate-x-[200%] transition-transform duration-1000`}
        ></div>
      </div>
    </div>
  );
};

const Dashboard = () => {
  const navigate = useNavigate();
  const [transferDialogOpen, setTransferDialogOpen] = useState(false);
  const [transferAmount, setTransferAmount] = useState("");
  const [isTransferring, setIsTransferring] = useState(false);
  const client = useQueryClient();
  const handleTransferSubmit = async () => {
    if (
      !transferAmount ||
      isNaN(transferAmount) ||
      Number(transferAmount) <= 0
    ) {
      toast.error("Please enter a valid amount", { id: "transfer-err" });
      return;
    }
    setIsTransferring(true);
    try {
      const res = await apiConnectorPost(
        endpoint?.fund_transfer_income_to_fund_wallet,
        { amount: Number(transferAmount) },
      );
      if (res?.data?.success) {
        client.refetchQueries("dashboard_api");
        client.refetchQueries("profile_api");
        toast.success(res?.data?.message || "Transfer successful!", {
          id: "transfer-ok",
        });
        setTransferDialogOpen(false);
        setTransferAmount("");
      } else {
        toast.error(res?.data?.message || "Transfer failed", {
          id: "transfer-fail",
        });
      }
    } catch {
      toast.error("Something went wrong", { id: "transfer-catch" });
    } finally {
      setIsTransferring(false);
    }
  };

  const functionTOCopy = (value) => {
    copy(value);
    // alert("Copied to clipboard!");
    toast.success("Copied to clipboard!", { id: 1 });
  };
  const { data: profile_data, isLoading: profileloading } = useQuery(
    ["profile_api"],
    () => apiConnectorGet(endpoint?.profile_api),
    {
      refetchOnMount: false,
      refetchOnReconnect: false,
      retry: false,
      retryOnMount: false,
      refetchOnWindowFocus: false,
    },
  );
  const user_profile = profile_data?.data?.result?.[0] || [];

  const { data: dashboard_Api, isLoading } = useQuery(
    ["dashboard_api"],
    () => apiConnectorGet(endpoint?.user_dashboard_api),
    {
      refetchOnMount: false,
      refetchOnReconnect: false,
      retry: false,
      retryOnMount: false,
      refetchOnWindowFocus: false,
    },
  );
  const dashboard = dashboard_Api?.data?.result?.[0] || [];
  const { data: dashboard_Business_Api, isLoading: isBusinessLoading } =
    useQuery(
      ["dashboard_business_api"],
      () => apiConnectorGet(endpoint?.user_dashboard_business_api),
      {
        refetchOnMount: false,
        refetchOnReconnect: false,
        retry: false,
        retryOnMount: false,
        refetchOnWindowFocus: false,
      },
    );
  const dashboardBusiness = dashboard_Business_Api?.data?.result || [];
  console.log("Dashboard Business Data:", dashboardBusiness);

  const { data: newsAndUpdatesApi, isLoading: isNewsLoading } = useQuery(
    ["get_news_and_updates"],
    () => apiConnectorGet(endpoint?.get_news_and_updates),
    {
      refetchOnMount: false,
      refetchOnReconnect: false,
      retry: false,
      retryOnMount: false,
      refetchOnWindowFocus: false,
    },
  );
  const newsAndUpdates = newsAndUpdatesApi?.data?.result || [];

  const { data: mt5AccountsApi, isLoading: isMt5Loading } = useQuery(
    ["get_mt5_account_admin", "dashboard"],
    () => apiConnectorGet(endpoint?.get_mt5_account_admin),
    {
      refetchOnMount: false,
      refetchOnReconnect: false,
      retry: false,
      retryOnMount: false,
      refetchOnWindowFocus: false,
    },
  );

  const mt5AccountRows = mt5AccountsApi?.data?.result;
  const activeMt5Accounts = (
    Array.isArray(mt5AccountRows)
      ? mt5AccountRows
      : Array.isArray(mt5AccountRows?.data)
        ? mt5AccountRows?.data
        : []
  ).filter(
    (item) =>
      Number(item?.m07_ac_status) === 1 && String(item?.m07_ac_no || "").trim(),
  );

  const isProfitActive = !!(
    user_profile?.tr03_topup_date &&
    String(user_profile?.tr03_topup_date).trim().toUpperCase() !== "NULL"
  );

  const teamStats = [
    {
      label: "Direct Member",
      value: user_profile?.tr03_dir_mem || 0,
      icon: <Diversity3Icon />,
      tone: "member",
      badge: "Network",
      caption: "Personal referrals",
      onClick: () => navigate("/referral"),
    },
    {
      label: "Team Member",
      value: user_profile?.tr03_team_mem || 0,
      icon: <Diversity3Icon />,
      tone: "member",
      badge: "Growth",
      caption: "Full downline size",
      onClick: () => navigate("/downline"),
    },
    {
      label: "Direct Business",
      value: getFloatingValue(dashboardBusiness?.direct_business),
      icon: <CurrencyExchangeIcon />,
      tone: "business",
      badge: "Volume",
      caption: "Frontline business",
    },
    {
      label: "Team Business",
      value: getFloatingValue(dashboardBusiness?.team_business),
      icon: <CurrencyExchangeIcon />,
      tone: "business",
      badge: "Turnover",
      caption: "Total team business",
    },
  ];

  const incomeItems = [
    {
      label: "Direct Sponsor Income",
      value: `$${getFloatingValue(dashboard?.referral_income)}`,
      path: "/income/direct",
      color: "#22D3EE",
      icon: <TrendingUpIcon fontSize="small" />,
    },
    {
      label: "Unilevel Income",
      value: `$${getFloatingValue(dashboard?.level_bonus || 0)}`,
      path: "/income/level",
      color: "#818CF8",
      icon: <BarChartIcon fontSize="small" />,
    },
    {
      label: "Global Circle Income",
      value: `$${getFloatingValue(dashboard?.roi_bonus || 0)}`,
      path: "/income/roi",
      color: "#34D399",
      icon: <AutoGraphIcon fontSize="small" />,
    },
    // { label: "Reward Bonus", value: `$${getFloatingValue(dashboard?.reward_bonus || 0)}`, path: "/income/reward", color: "#F472B6", icon: <PieChartIcon fontSize="small" /> },
    // { label: "Salary Bonus", value: `$${getFloatingValue(dashboard?.salary_bonus || 0)}`, path: "/income/salary", color: "#FBBF24", icon: <InsightsIcon fontSize="small" /> },
    // { label: "Total Income", value: `$${getFloatingValue(dashboard?.total_income || 0)}`, path: "", color: "#2563EB", icon: <StackedBarChartIcon fontSize="small" /> },
  ];

  const quickActions = [
    {
      label: "Topup",
      color: "#2563EB",
      path: "/fund-transfer-to-topup-wallet",
      icon: (
        <svg
          className="w-5 h-5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 4v16m8-8H4"
          />
        </svg>
      ),
    },
    {
      label: "Deposit",
      color: "#22D3EE",
      path: "/topup_data",
      icon: (
        <svg
          className="w-5 h-5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z"
          />
        </svg>
      ),
    },
    {
      label: "Withdraw",
      color: "#F472B6",
      path: "/with",
      icon: (
        <svg
          className="w-5 h-5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M5 10l7-7m0 0l7 7m-7-7v18"
          />
        </svg>
      ),
    },
    {
      label: "Referral",
      color: "#34D399",
      path: "/referral",
      icon: (
        <svg
          className="w-5 h-5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z"
          />
        </svg>
      ),
    },
  ];

  return (
    <>
      <div className="min-h-screen text-white px-3 sm:px-5 pb-10 relative overflow-x-hidden bg-gradient-to-br from-[#060E1C] via-[#080F1C] to-[#060E1C]">
        {/* Background */}
        <div className="pointer-events-none fixed inset-0 bg-[radial-gradient(circle_at_50%_0%,rgba(37,99,235,0.12),transparent_55%)]"></div>
        <div
          className="pointer-events-none fixed inset-0"
          style={{
            backgroundImage: `linear-gradient(rgba(37,99,235,0.06) 1px, transparent 1px), linear-gradient(90deg, rgba(37,99,235,0.06) 1px, transparent 1px)`,
            backgroundSize: "40px 40px",
          }}
        ></div>
        <div className="pointer-events-none fixed top-0 left-0 w-96 h-96 bg-[#2563EB]/8 rounded-full blur-3xl animate-pulse"></div>
        <div
          className="pointer-events-none fixed bottom-0 right-0 w-96 h-96 bg-[#1D4ED8]/8 rounded-full blur-3xl animate-pulse"
          style={{ animationDelay: "1.5s" }}
        ></div>

        <Loader
          isLoading={
            profileloading || isLoading || isNewsLoading || isMt5Loading
          }
        />

        {/* ── HEADER ── */}
        <div className="relative z-10 flex items-center justify-between pt-4 pb-3">
          <div>
            <p className="text-[#475569] text-[10px] tracking-[0.3em] uppercase mb-0.5">
              Hexafund
            </p>
            <h1 className="text-2xl font-black text-white tracking-tight">
              Overview
            </h1>
          </div>
          <div className="flex items-center gap-2">
            <span className="flex items-center gap-1.5 text-[10px] text-[#22D3EE] bg-[#22D3EE]/10 border border-[#22D3EE]/25 rounded-full px-3 py-1.5 font-semibold tracking-widest uppercase">
              <span className="w-1.5 h-1.5 rounded-full bg-[#22D3EE] animate-pulse"></span>
              Live
            </span>
            <span className="text-[10px] text-[#475569] bg-[#071526]/60 border border-[#1e3a5f]/50 rounded-full px-3 py-1.5 font-mono tracking-wider">
              {user_profile?.lgn_cust_id || "—"}
            </span>
          </div>
        </div>

        <NewsTicker
          items={newsAndUpdates
            ?.filter((i) => i.m01_nw_status)
            ?.map((i) => i.m01_nw_news)}
        />
        {/* ══════════════════════════════════════
            HERO BALANCE CARD  (credit-card style)
        ══════════════════════════════════════ */}
        <div className="relative mt-5 mb-5 rounded-3xl overflow-hidden shadow-2xl shadow-[#2563EB]/30">
          {/* gradient background */}
          <div className="absolute inset-0 bg-gradient-to-br from-[#1D4ED8] via-[#2563EB] to-[#0B1E36]"></div>
          {/* holographic shimmer */}
          <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/5 to-transparent"></div>
          {/* decorative circles */}
          <div className="absolute -top-16 -right-16 w-56 h-56 bg-white/5 rounded-full"></div>
          <div className="absolute -bottom-10 -left-10 w-40 h-40 bg-white/5 rounded-full"></div>
          <div className="absolute top-1/2 right-1/4 w-24 h-24 bg-[#22D3EE]/10 rounded-full blur-xl"></div>
          {/* top shimmer line */}
          <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/30 to-transparent"></div>

          <div className="relative z-10 p-5 sm:p-8">
            {/* Card header row */}
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center gap-2">
                <div className="w-9 h-7 rounded-md bg-gradient-to-br from-white/30 to-white/10 border border-white/20 flex items-center justify-center">
                  <svg
                    className="w-4 h-4 text-white/80"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.5}
                      d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
                    />
                  </svg>
                </div>
                <span className="text-white/60 text-xs tracking-[0.2em] uppercase font-medium">
                  Hexafund Wallet
                </span>
              </div>
              <img src={tether} alt="USDT" className="w-8 h-8 opacity-80" />
            </div>

            {/* Balance */}
            <div className="mb-5">
              <p className="text-white/50 text-xs mb-1 tracking-widest uppercase">
                Current Balance
              </p>
              <div className="flex items-end gap-2">
                <h2 className="text-4xl sm:text-5xl font-black text-white tracking-tight">
                  {getFloatingValue(user_profile?.tr03_inc_wallet)}
                </h2>
                <span className="text-white/50 text-lg font-bold mb-1">
                  USD
                </span>
              </div>
              <p className="text-[#22D3EE]/80 text-xs mt-1 tracking-wider">
                USDT · BNB Smart Chain
              </p>
            </div>

            {/* Account ID */}
            <p className="text-white/30 text-xs tracking-[0.35em] font-mono mb-5">
              {user_profile?.lgn_cust_id
                ? user_profile.lgn_cust_id
                    .toString()
                    .padStart(8, "0")
                    .replace(/(.{4})/g, "$1 ")
                    .trim()
                : "•••• •••• ••••"}
            </p>

            {/* Action buttons */}
            <div className="flex flex-wrap gap-2">
              {[
                { label: "Withdraw", path: "/with", icon: "↑" },
                {
                  label: "Transfer",
                  onClick: () => setTransferDialogOpen(true),
                  icon: "⇄",
                },
                { label: "Deposit", path: "/topup_data", icon: "↓" },
                {
                  label: "Topup",
                  path: "/fund-transfer-to-topup-wallet",
                  icon: "+",
                },
              ].map((btn) => (
                <button
                  key={btn.label}
                  onClick={btn.onClick || (() => navigate(btn.path))}
                  className="flex items-center gap-1.5 bg-white/10 hover:bg-white/20 active:scale-95 border border-white/20 rounded-2xl px-3 py-2 text-xs font-semibold text-white transition-all duration-200 backdrop-blur-sm"
                >
                  <span className="text-base leading-none">{btn.icon}</span>
                  {btn.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* ══════════════════════════════════════
            3 QUICK STAT PILLS
        ══════════════════════════════════════ */}
        <div className="grid grid-cols-3 gap-2 sm:gap-3 mb-5">
          {[
            {
              label: "Topup Wallet",
              value: getFloatingValue(user_profile?.tr03_topup_wallet),
              color: "#2563EB",
              action: () => navigate("/fund-transfer-to-topup-wallet"),
              actionLabel: "Topup",
            },
            {
              label: "Fund Wallet",
              value: getFloatingValue(user_profile?.tr03_fund_wallet),
              color: "#22D3EE",
              action: () => navigate("/topup_data"),
              actionLabel: "Add",
            },
            {
              label: "Total Income",
              value: getFloatingValue(dashboard?.total_income || 0),
              color: "#818CF8",
              action: null,
              actionLabel: null,
            },
          ].map((stat) => (
            <div
              key={stat.label}
              className="relative rounded-2xl p-3 sm:p-4 overflow-hidden border border-white/8 backdrop-blur-sm"
              style={{
                background: `linear-gradient(135deg, ${stat.color}18 0%, #071526 100%)`,
                borderColor: `${stat.color}25`,
              }}
            >
              <div
                className="absolute top-0 left-0 right-0 h-px"
                style={{
                  background: `linear-gradient(90deg, transparent, ${stat.color}50, transparent)`,
                }}
              ></div>
              <p className="text-[10px] sm:text-xs text-[#64748B] uppercase tracking-widest mb-1 truncate">
                {stat.label}
              </p>
              <p className="text-base sm:text-xl font-black text-white truncate">
                ${stat.value}
              </p>
              {stat.action && (
                <button
                  onClick={stat.action}
                  className="mt-2 text-[9px] sm:text-[10px] font-bold uppercase tracking-widest rounded-full px-2 py-0.5 transition-all duration-200"
                  style={{
                    color: stat.color,
                    background: `${stat.color}18`,
                    border: `1px solid ${stat.color}30`,
                  }}
                >
                  {stat.actionLabel} →
                </button>
              )}
            </div>
          ))}
        </div>

        {/* ══════════════════════════════════════
            QUICK ACTION ICON BUTTONS
        ══════════════════════════════════════ */}
        <div className="grid grid-cols-4 gap-2 sm:gap-3 mb-6">
          {quickActions.map((action) => (
            <button
              key={action.label}
              onClick={() => navigate(action.path)}
              className="flex flex-col items-center gap-2 rounded-2xl py-3 sm:py-4 border border-white/8 hover:border-white/15 active:scale-95 transition-all duration-200 backdrop-blur-sm group"
              style={{
                background: `${action.color}12`,
                borderColor: `${action.color}20`,
              }}
            >
              <div
                className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl flex items-center justify-center transition-transform duration-200 group-hover:scale-110"
                style={{ background: `${action.color}20`, color: action.color }}
              >
                {action.icon}
              </div>
              <span className="text-[10px] sm:text-xs font-semibold text-[#94A3B8] tracking-wide">
                {action.label}
              </span>
            </button>
          ))}
        </div>

        {/* ══════════════════════════════════════
            REFERRAL + NETWORK  (2-col)
        ══════════════════════════════════════ */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
          {/* Referral Card */}
          <div
            className="rounded-2xl border border-[#2563EB]/20 overflow-hidden backdrop-blur-sm"
            style={{
              background: "linear-gradient(135deg, #071526 0%, #0B1E36 100%)",
            }}
          >
            <div
              className="absolute inset-0 pointer-events-none rounded-2xl"
              style={{
                background:
                  "radial-gradient(circle at top right, rgba(37,99,235,0.08), transparent 60%)",
              }}
            ></div>
            <div className="p-5">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 rounded-xl bg-[#2563EB]/15 flex items-center justify-center">
                  <svg
                    className="w-4 h-4 text-[#2563EB]"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"
                    />
                  </svg>
                </div>
                <div>
                  <p className="text-white text-sm font-bold">Referral Link</p>
                  <p className="text-[#475569] text-[10px]">Share & earn</p>
                </div>
              </div>

              <div
                className="rounded-xl p-3 mb-3 cursor-pointer group/link hover:opacity-90 transition-opacity"
                style={{
                  background: "rgba(37,99,235,0.08)",
                  border: "1px solid rgba(37,99,235,0.15)",
                }}
                onClick={() =>
                  functionTOCopy(
                    frontend +
                      "/register?startapp=" +
                      user_profile?.lgn_cust_id,
                  )
                }
              >
                <p className="text-[#7DD3FC] text-xs break-all leading-relaxed">
                  {frontend + "/register?startapp=" + user_profile?.lgn_cust_id}
                </p>
              </div>

              <button
                onClick={() =>
                  functionTOCopy(
                    frontend +
                      "/register?startapp=" +
                      user_profile?.lgn_cust_id,
                  )
                }
                className="w-full py-2.5 rounded-xl text-sm font-bold text-white transition-all duration-200 hover:brightness-110 active:scale-[0.98]"
                style={{
                  background: "linear-gradient(135deg, #1D4ED8, #2563EB)",
                }}
              >
                Copy Link
              </button>

              <div className="mt-3 pt-3 border-t border-[#1e3a5f]/50 text-center">
                <p className="text-[#475569] text-[10px] uppercase tracking-widest mb-1">
                  Your ID
                </p>
                <p className="text-[#22D3EE] font-mono text-sm font-semibold">
                  {user_profile?.lgn_cust_id || "N/A"}
                </p>
              </div>
            </div>
          </div>

          {/* Network Stats Card */}
          <div
            className="rounded-2xl border border-[#2563EB]/20 overflow-hidden backdrop-blur-sm"
            style={{
              background: "linear-gradient(135deg, #071526 0%, #0B1E36 100%)",
            }}
          >
            <div className="p-5">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 rounded-xl bg-[#818CF8]/15 flex items-center justify-center">
                  <Diversity3Icon style={{ fontSize: 18, color: "#818CF8" }} />
                </div>
                <div>
                  <p className="text-white text-sm font-bold">Network Stats</p>
                  <p className="text-[#475569] text-[10px]">Team overview</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                {[
                  {
                    label: "Direct Members",
                    value: user_profile?.tr03_dir_mem || 0,
                    color: "#22D3EE",
                    unit: "people",
                    path: "/referral",
                  },
                  {
                    label: "Team Members",
                    value: user_profile?.tr03_team_mem || 0,
                    color: "#818CF8",
                    unit: "people",
                    path: "/downline",
                  },
                  {
                    label: "Direct Business",
                    value: `$${getFloatingValue(dashboardBusiness?.direct_business)}`,
                    color: "#34D399",
                    unit: "USD",
                    path: null,
                  },
                  {
                    label: "Team Business",
                    value: `$${getFloatingValue(dashboardBusiness?.team_business)}`,
                    color: "#FBBF24",
                    unit: "USD",
                    path: null,
                  },
                ].map((stat) => (
                  <div
                    key={stat.label}
                    onClick={() => stat.path && navigate(stat.path)}
                    className={`rounded-xl p-3 ${stat.path ? "cursor-pointer hover:brightness-110" : ""} transition-all duration-200`}
                    style={{
                      background: `${stat.color}10`,
                      border: `1px solid ${stat.color}20`,
                    }}
                  >
                    <p className="text-[9px] sm:text-[10px] text-[#64748B] uppercase tracking-widest mb-1 leading-tight">
                      {stat.label}
                    </p>
                    <p
                      className="text-lg sm:text-xl font-black"
                      style={{ color: stat.color }}
                    >
                      {stat.value}
                    </p>
                    <p className="text-[9px] text-[#475569] mt-0.5">
                      {stat.unit}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* ══════════════════════════════════════
            INCOME OVERVIEW  (new row-based layout)
        ══════════════════════════════════════ */}
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-3">
            <p className="text-[10px] text-[#475569] uppercase tracking-[0.3em] font-semibold">
              Income Overview
            </p>
            <div className="flex-1 h-px bg-gradient-to-r from-[#2563EB]/20 to-transparent"></div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
            {incomeItems.map((item) => (
              <div
                key={item.label}
                onClick={() => item.path && navigate(item.path)}
                className={`relative rounded-2xl p-4 overflow-hidden group ${item.path ? "cursor-pointer" : ""} transition-all duration-300 hover:-translate-y-0.5`}
                style={{
                  background:
                    "linear-gradient(135deg, #071526 0%, #0B1E36 80%)",
                  border: `1px solid ${item.color}25`,
                }}
              >
                {/* Left color bar */}
                <div
                  className="absolute left-0 top-3 bottom-3 w-1 rounded-full"
                  style={{ background: item.color }}
                ></div>
                {/* Glow */}
                <div
                  className="absolute top-0 right-0 w-24 h-24 rounded-full blur-2xl opacity-20 transition-opacity duration-300 group-hover:opacity-40"
                  style={{ background: item.color }}
                ></div>

                <div className="relative z-10 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div
                      className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
                      style={{
                        background: `${item.color}18`,
                        color: item.color,
                      }}
                    >
                      {item.icon}
                    </div>
                    <div>
                      <p className="text-[#64748B] text-[10px] uppercase tracking-widest font-medium">
                        {item.label}
                      </p>
                      <p className="text-xl font-black text-white mt-0.5">
                        {item.value}
                      </p>
                    </div>
                  </div>
                  {item.path && (
                    <svg
                      className="w-4 h-4 opacity-30 group-hover:opacity-70 group-hover:translate-x-0.5 transition-all duration-200"
                      style={{ color: item.color }}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 5l7 7-7 7"
                      />
                    </svg>
                  )}
                </div>

                {/* Bottom gradient bar */}
                <div className="relative z-10 mt-3 h-0.5 rounded-full overflow-hidden bg-white/5">
                  <div
                    className="h-full w-full rounded-full opacity-60"
                    style={{
                      background: `linear-gradient(90deg, ${item.color}, transparent)`,
                    }}
                  ></div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* OLD top grid (hidden — kept here to avoid breaking other refs) */}
        <div className="hidden">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
            {/* Active Topup & Balance Card */}
            <div className="bg-gradient-to-br from-[#060E1C] via-[#080F1C] to-[#0B1E36] rounded-2xl p-4 sm:p-6 border border-[#2563EB]/30 shadow-xl shadow-[#2563EB]/10 relative overflow-hidden group hover:shadow-[#2563EB]/20 hover:-translate-y-0.5 transition-all duration-300">
              {/* Animated background effects */}
              <div className="absolute top-0 right-0 w-40 h-40 bg-[#2563EB]/5 rounded-full blur-3xl group-hover:bg-[#2563EB]/10 transition-all duration-500"></div>
              <div className="absolute -bottom-10 -left-10 w-32 h-32 bg-[#1D4ED8]/5 rounded-full blur-2xl"></div>

              {/* Accent line */}
              <div className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-[#22D3EE] via-[#2563EB] to-[#1D4ED8]"></div>

              {/* Decorative corner elements */}
              <div className="absolute top-0 right-0 w-20 h-20 border-t-2 border-r-2 border-[#2563EB]/20 rounded-tr-2xl"></div>
              <div className="absolute bottom-0 left-0 w-16 h-16 border-b-2 border-l-2 border-[#1D4ED8]/10 rounded-bl-2xl"></div>

              {/* Shine effect on hover */}
              <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none">
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-[#2563EB]/5 to-transparent -skew-x-12 translate-x-[-200%] group-hover:translate-x-[200%] transition-transform duration-1000"></div>
              </div>

              {/* Particles */}
              <div className="absolute top-10 left-10 w-1 h-1 bg-[#2563EB] rounded-full opacity-60 animate-ping"></div>
              <div
                className="absolute bottom-20 right-16 w-1 h-1 bg-[#22D3EE] rounded-full opacity-60 animate-ping"
                style={{ animationDelay: "1s" }}
              ></div>
              <div
                className="absolute top-1/2 left-20 w-1 h-1 bg-[#1D4ED8] rounded-full opacity-60 animate-ping"
                style={{ animationDelay: "2s" }}
              ></div>

              {/* ===== TOP SECTION - Active Topup ===== */}
              <div className="relative z-10 mb-5">
                <div className="flex items-start justify-between">
                  <div>
                    {/* Label */}
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-2 h-2 rounded-full bg-[#2563EB] animate-pulse"></div>
                      <p className="text-gray-400 text-sm font-medium tracking-wide uppercase">
                        Topup Wallet
                      </p>
                    </div>

                    {/* Value */}
                    <h2 className="text-3xl font-bold mb-1">
                      <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#22D3EE] via-[#2563EB] to-[#1D4ED8]">
                        {getFloatingValue(user_profile?.tr03_topup_wallet)}USD
                      </span>
                    </h2>

                    {/* Tether icon row */}
                    <div className="flex items-center gap-2 mt-1">
                      <div className=" rounded-full  flex items-center justify-center ">
                        <img src={tether} alt="" className="w-6 h-6" />
                      </div>
                      <span className="text-xs text-gray-500 font-medium">
                        Active Topup
                      </span>
                    </div>
                  </div>

                  {/* Deposit Button with icon glow area */}
                  <div className="relative">
                    <div className="absolute inset-0 bg-[#2563EB]/20 rounded-full blur-xl animate-pulse"></div>
                    <button
                      onClick={() => navigate("/fund-transfer-to-topup-wallet")}
                      className="relative bg-gradient-to-r from-[#2563EB] to-[#1D4ED8] hover:from-[#00c8d4] hover:to-[#5edb89] text-white font-semibold text-sm px-5 py-2 rounded-full shadow-lg shadow-[#2563EB]/40 transition-all duration-300 hover:scale-105 hover:shadow-[#22D3EE]/60 group/btn overflow-hidden"
                    >
                      <div className="absolute inset-0 opacity-0 group-hover/btn:opacity-100 transition-opacity duration-300">
                        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -skew-x-12 translate-x-[-200%] group-hover/btn:translate-x-[200%] transition-transform duration-500"></div>
                      </div>
                      <span className="relative z-10">Topup</span>
                    </button>
                  </div>
                </div>

                {/* Notice text */}
                <div className="mt-4 bg-[#071526]/30 rounded-lg py-2 px-3 border border-[#2563EB]/10">
                  <p className="text-[#22D3EE]/70 text-xs text-center italic">
                    Topup/Re-Topup Your Account to continue earning profit &amp;
                    Rewards
                  </p>
                </div>
              </div>

              {/* Divider */}
              <div className="relative z-10 flex items-center gap-3 mb-5">
                <div className="flex-1 h-px bg-gradient-to-r from-transparent via-[#2563EB]/40 to-transparent"></div>
                <div className="w-1.5 h-1.5 rounded-full bg-[#2563EB]"></div>
                <div className="flex-1 h-px bg-gradient-to-r from-transparent via-[#2563EB]/40 to-transparent"></div>
              </div>

              {/* ===== MIDDLE SECTION - Fund Wallet (new) ===== */}
              <div className="relative z-10 mb-5">
                <div className="flex items-start justify-between">
                  <div>
                    {/* Label */}
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-2 h-2 rounded-full bg-[#2563EB] animate-pulse"></div>
                      <p className="text-gray-400 text-sm font-medium tracking-wide uppercase">
                        Fund Wallet
                      </p>
                    </div>

                    {/* Value */}
                    <h2 className="text-2xl font-bold mb-1">
                      <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#22D3EE] via-[#2563EB] to-[#1D4ED8]">
                        ${getFloatingValue(user_profile?.tr03_fund_wallet)}USD
                      </span>
                    </h2>

                    {/* Tether icon row */}
                    <div className="flex items-center gap-2 mt-1">
                      <div className=" rounded-full  flex items-center justify-center ">
                        <img src={tether} alt="" className="w-6 h-6" />
                      </div>
                      <span className="text-xs text-gray-500 font-medium">
                        Available Fund
                      </span>
                    </div>
                  </div>

                  {/* Transfer Button */}
                  <div className="relative">
                    <div className="absolute inset-0 bg-[#2563EB]/20 rounded-full blur-xl animate-pulse"></div>
                    <button
                      onClick={() => navigate("/topup_data")}
                      className="relative bg-gradient-to-r from-[#2563EB] to-[#1D4ED8] hover:from-[#00c8d4] hover:to-[#5edb89] text-white font-semibold text-sm px-5 py-2 rounded-full shadow-lg shadow-[#2563EB]/40 transition-all duration-300 hover:scale-105 hover:shadow-[#22D3EE]/60 group/btn overflow-hidden"
                    >
                      <div className="absolute inset-0 opacity-0 group-hover/btn:opacity-100 transition-opacity duration-300">
                        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -skew-x-12 translate-x-[-200%] group-hover/btn:translate-x-[200%] transition-transform duration-500"></div>
                      </div>
                      <span className="relative z-10">Deposit</span>
                    </button>
                  </div>
                </div>
              </div>

              {/* ===== BOTTOM SECTION - Balance ===== */}
              <div className="relative z-10">
                <div className="flex items-center justify-between">
                  <div>
                    {/* Label */}
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-2 h-2 rounded-full bg-[#1D4ED8] animate-pulse"></div>
                      <svg
                        className="w-4 h-4 text-[#1D4ED8]"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                        />
                      </svg>
                      <p className="text-gray-400 text-sm font-medium tracking-wide uppercase">
                        Current Balance
                      </p>
                    </div>

                    {/* Value */}
                    <h2 className="text-3xl font-bold mb-1">
                      <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#22D3EE] via-[#2563EB] to-[#1D4ED8]">
                        {getFloatingValue(user_profile?.tr03_inc_wallet)} USD
                      </span>
                    </h2>

                    {/* Tether icon row */}
                    <div className="flex items-center gap-2 mt-1">
                      <div className=" rounded-full  flex items-center justify-center ">
                        <img src={tether} alt="" className="w-6 h-6" />
                      </div>
                      <span className="text-xs text-gray-500 font-medium">
                        Wallet Balance
                      </span>
                    </div>
                  </div>

                  {/* Withdrawal & Transfer Buttons */}
                  <div className="flex flex-col gap-2 items-end">
                    <div className="relative">
                      <div className="absolute inset-0 bg-[#2563EB]/20 rounded-full blur-xl animate-pulse"></div>
                      <button
                        onClick={() => navigate("/with")}
                        className="relative bg-gradient-to-r from-[#2563EB] to-[#1D4ED8] hover:from-[#00c8d4] hover:to-[#22D3EE] text-white font-bold text-sm px-5 py-2 rounded-full shadow-lg shadow-[#2563EB]/40 transition-all duration-300 hover:scale-105 hover:shadow-[#1D4ED8]/60 group/btn overflow-hidden"
                      >
                        <div className="absolute inset-0 opacity-0 group-hover/btn:opacity-100 transition-opacity duration-300">
                          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -skew-x-12 translate-x-[-200%] group-hover/btn:translate-x-[200%] transition-transform duration-500"></div>
                        </div>
                        <span className="relative z-10">Withdrawal</span>
                      </button>
                    </div>
                    <div className="relative">
                      <div className="absolute inset-0 bg-[#1D4ED8]/20 rounded-full blur-xl animate-pulse"></div>
                      <button
                        onClick={() => setTransferDialogOpen(true)}
                        className="relative bg-gradient-to-r from-[#1D4ED8] to-[#2563EB] hover:from-[#1db357] hover:to-[#00c8d4] text-white font-bold text-sm px-5 py-2 rounded-full shadow-lg shadow-[#1D4ED8]/40 transition-all duration-300 hover:scale-105 hover:shadow-[#2563EB]/60 group/btn overflow-hidden"
                      >
                        <div className="absolute inset-0 opacity-0 group-hover/btn:opacity-100 transition-opacity duration-300">
                          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -skew-x-12 translate-x-[-200%] group-hover/btn:translate-x-[200%] transition-transform duration-500"></div>
                        </div>
                        <span className="relative z-10">Transfer</span>
                      </button>
                    </div>
                  </div>
                </div>

                {/* Progress Bar */}
                <div className="mt-4 flex items-center gap-2">
                  <div className="flex-1 h-1.5 bg-gray-800 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-[#2563EB] to-[#1D4ED8] rounded-full transition-all duration-1000 ease-out shadow-lg shadow-[#2563EB]/50"
                      style={{
                        width: `${Number(user_profile?.tr03_inc_wallet || 0) > 0 ? "100" : "0"}%`,
                      }}
                    ></div>
                  </div>
                  <span className="text-xs text-[#2563EB] font-semibold">
                    {Number(user_profile?.tr03_inc_wallet || 0) > 0
                      ? "100%"
                      : "0%"}
                  </span>
                </div>
              </div>
            </div>
            {/* Referral */}
            <div className="bg-gradient-to-br from-[#060E1C] via-[#080F1C] to-[#0B1E36] rounded-2xl p-4 sm:p-6 border border-[#2563EB]/30 shadow-xl shadow-[#2563EB]/10 relative overflow-hidden group hover:shadow-[#2563EB]/20 hover:-translate-y-0.5 transition-all duration-300">
              {/* Animated background effects */}
              <div className="absolute top-0 right-0 w-40 h-40 bg-[#2563EB]/5 rounded-full blur-3xl group-hover:bg-[#2563EB]/10 transition-all duration-500"></div>
              <div className="absolute -bottom-10 -left-10 w-32 h-32 bg-[#1D4ED8]/5 rounded-full blur-2xl"></div>

              {/* Accent line */}
              <div className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-[#22D3EE] via-[#2563EB] to-[#1D4ED8]"></div>

              {/* Decorative corner elements */}
              <div className="absolute top-0 right-0 w-24 h-24 border-t-2 border-r-2 border-[#2563EB]/20 rounded-tr-2xl"></div>
              <div className="absolute bottom-0 left-0 w-20 h-20 border-b-2 border-l-2 border-[#1D4ED8]/10 rounded-bl-2xl"></div>

              {/* Decorative link icon */}
              <div className="absolute top-4 right-4 w-10 h-10 rounded-full bg-[#2563EB]/10 flex items-center justify-center border border-[#2563EB]/30">
                <svg
                  className="w-5 h-5 text-[#2563EB]"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"
                  />
                </svg>
              </div>

              {/* Content */}
              <div className="relative z-10">
                {/* Header */}
                <div className="flex items-center justify-center gap-2 mb-3">
                  <div className="w-2 h-2 rounded-full bg-[#2563EB] animate-pulse"></div>
                  <p className="text-gray-400 text-sm font-medium tracking-wide uppercase">
                    Referral Link
                  </p>
                  <div className="w-2 h-2 rounded-full bg-[#1D4ED8] animate-pulse"></div>
                </div>

                {/* Link Display Box */}
                <div className="bg-gradient-to-r from-[#071526]/30 to-[#0B1E36]/20 rounded-lg p-4 mb-4 border border-[#2563EB]/20 backdrop-blur-sm relative overflow-hidden group/link">
                  {/* Shine effect */}
                  <div className="absolute inset-0 opacity-0 group-hover/link:opacity-100 transition-opacity duration-500">
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-[#2563EB]/10 to-transparent -skew-x-12 translate-x-[-200%] group-hover/link:translate-x-[200%] transition-transform duration-1000"></div>
                  </div>

                  <p
                    onClick={() =>
                      functionTOCopy(
                        frontend +
                          "/register?startapp=" +
                          user_profile?.lgn_cust_id,
                      )
                    }
                    className="text-transparent bg-clip-text bg-gradient-to-r from-[#22D3EE] via-[#2563EB] to-[#1D4ED8] text-sm break-all text-center font-medium cursor-pointer hover:scale-[1.02] transition-transform duration-200 relative z-10"
                  >
                    {frontend +
                      "/register?startapp=" +
                      user_profile?.lgn_cust_id}
                  </p>
                </div>

                {/* Copy Button */}
                <div className="flex justify-center mb-5">
                  <button
                    onClick={() =>
                      functionTOCopy(
                        frontend +
                          "/register?startapp=" +
                          user_profile?.lgn_cust_id,
                      )
                    }
                    className="relative px-6 py-2.5 rounded-lg font-semibold text-sm overflow-hidden group/btn transition-all duration-300 hover:scale-105"
                  >
                    {/* Button background */}
                    <div className="absolute inset-0 bg-gradient-to-r from-[#2563EB] to-[#1D4ED8] transition-transform duration-300 group-hover/btn:scale-105"></div>

                    {/* Button glow */}
                    <div className="absolute inset-0 bg-gradient-to-r from-[#2563EB] to-[#22D3EE] opacity-0 group-hover/btn:opacity-100 transition-opacity duration-300 blur-md"></div>

                    {/* Button content */}
                    <span className="relative z-10 flex items-center gap-2 text-white">
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                        />
                      </svg>
                      Copy Link
                    </span>

                    {/* Shine effect */}
                    <div className="absolute inset-0 opacity-0 group-hover/btn:opacity-100 transition-opacity duration-300">
                      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -skew-x-12 translate-x-[-200%] group-hover/btn:translate-x-[200%] transition-transform duration-500"></div>
                    </div>
                  </button>
                </div>

                {/* Divider */}
                <div className="flex items-center gap-3 mb-4">
                  <div className="flex-1 h-px bg-gradient-to-r from-transparent via-[#2563EB]/30 to-transparent"></div>
                  <div className="w-1.5 h-1.5 rounded-full bg-[#2563EB]"></div>
                  <div className="flex-1 h-px bg-gradient-to-r from-transparent via-[#2563EB]/30 to-transparent"></div>
                </div>

                {/* Address Section */}
                <div className="text-center">
                  <p className="text-gray-400 text-xs font-medium tracking-wide uppercase mb-2">
                    My Customer ID
                  </p>
                  <div className="bg-gradient-to-r from-[#071526]/20 to-[#0B1E36]/10 rounded-lg p-3 border border-[#2563EB]/10 backdrop-blur-sm">
                    <p className="text-[#2563EB] text-sm break-all font-medium">
                      {user_profile?.lgn_cust_id || "N/A"}
                    </p>
                  </div>
                </div>
              </div>

              {/* Particles effect */}
              <div className="absolute top-10 left-10 w-1 h-1 bg-[#2563EB] rounded-full opacity-60 animate-ping"></div>
              <div
                className="absolute bottom-20 right-16 w-1 h-1 bg-[#22D3EE] rounded-full opacity-60 animate-ping"
                style={{ animationDelay: "1s" }}
              ></div>
              <div
                className="absolute top-1/2 left-20 w-1 h-1 bg-[#1D4ED8] rounded-full opacity-60 animate-ping"
                style={{ animationDelay: "2s" }}
              ></div>

              {/* Shine effect on hover */}
              <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-[#2563EB]/5 to-transparent -skew-x-12 translate-x-[-200%] group-hover:translate-x-[200%] transition-transform duration-1000"></div>
              </div>
            </div>

            {/* Team Stats Card */}
            <div className="bg-gradient-to-br from-[#060E1C] via-[#080F1C] to-[#0B1E36] rounded-2xl p-4 sm:p-6 border border-[#2563EB]/30 shadow-xl shadow-[#2563EB]/10 relative overflow-hidden group hover:shadow-[#2563EB]/20 hover:-translate-y-0.5 transition-all duration-300 mb-6">
              {/* Animated background effects */}
              <div className="absolute top-0 right-0 w-40 h-40 bg-[#2563EB]/5 rounded-full blur-3xl group-hover:bg-[#2563EB]/10 transition-all duration-500"></div>
              <div className="absolute -bottom-10 -left-10 w-32 h-32 bg-[#1D4ED8]/5 rounded-full blur-2xl"></div>

              {/* Accent line */}
              <div className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-[#22D3EE] via-[#2563EB] to-[#1D4ED8]"></div>

              {/* Decorative corner elements */}
              <div className="absolute top-0 right-0 w-20 h-20 border-t-2 border-r-2 border-[#2563EB]/20 rounded-tr-2xl"></div>
              <div className="absolute bottom-0 left-0 w-16 h-16 border-b-2 border-l-2 border-[#1D4ED8]/10 rounded-bl-2xl"></div>

              {/* Team Icon */}
              <div className="relative z-10 mb-6 rounded-[28px] border border-[#2563EB]/15 bg-gradient-to-r from-[#071526]/25 via-[#071526]/60 to-[#102319]/25 px-4 py-5 backdrop-blur-sm">
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(0,180,190,0.12),transparent_55%)]"></div>
                <div className="relative flex items-center gap-4">
                  <div className="relative flex h-20 w-20 items-center justify-center rounded-[24px] border border-[#2563EB]/30 bg-[#071526]/90 shadow-lg shadow-[#2563EB]/10">
                    <div className="absolute inset-0 rounded-[24px] bg-gradient-to-br from-[#2563EB]/18 via-transparent to-[#1D4ED8]/18"></div>
                    <div className="absolute inset-3 rounded-[18px] border border-[#22D3EE]/20"></div>
                    <div className="absolute inset-0 rounded-[24px] bg-[#2563EB]/20 blur-xl"></div>
                    <div className="relative flex items-center justify-center text-[#2563EB]">
                      <Diversity3Icon className="!text-5xl" />
                    </div>
                  </div>

                  <div className="min-w-0 flex-1">
                    <div className="mb-2 flex items-center gap-2">
                      <div className="h-2 w-2 rounded-full bg-[#2563EB] animate-pulse"></div>
                      <p className="text-[11px] font-semibold uppercase tracking-[0.3em] text-[#22D3EE]/80">
                        Team Matrix
                      </p>
                    </div>
                    <h3 className="text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-[#22D3EE] via-[#2563EB] to-[#1D4ED8]">
                      Network Snapshot
                    </h3>
                    <p className="mt-1 text-xs text-gray-400">
                      Members and business volume in one compact view.
                    </p>
                  </div>
                </div>
              </div>

              {/* Stats List */}
              <div className="relative z-10 grid grid-cols-1 gap-3 sm:grid-cols-2">
                {teamStats.map((item) => {
                  const isBusiness = item.tone === "business";

                  return (
                    <button
                      key={item.label}
                      type="button"
                      onClick={item.onClick}
                      className={`group/stat relative overflow-hidden rounded-2xl border p-4 text-left transition-all duration-300 ${
                        item.onClick
                          ? "cursor-pointer hover:-translate-y-1"
                          : "cursor-default"
                      } ${
                        isBusiness
                          ? "border-[#1D4ED8]/20 bg-gradient-to-br from-[#060E1C]/90 via-[#080F1C]/95 to-[#0B1E36]/95 hover:border-[#2563EB]/35"
                          : "border-[#2563EB]/20 bg-gradient-to-br from-[#060E1C]/90 via-[#071526]/95 to-[#0B1E36]/95 hover:border-[#2563EB]/35"
                      }`}
                    >
                      <div
                        className={`absolute inset-0 opacity-0 transition-opacity duration-300 group-hover/stat:opacity-100 ${isBusiness ? "bg-[radial-gradient(circle_at_top_right,rgba(33,193,92,0.14),transparent_55%)]" : "bg-[radial-gradient(circle_at_top_right,rgba(0,180,190,0.16),transparent_55%)]"}`}
                      ></div>
                      <div className="absolute inset-x-4 bottom-0 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent"></div>

                      <div className="relative flex items-start justify-between gap-3">
                        <div className="flex min-w-0 items-start gap-3">
                          <div
                            className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl border ${isBusiness ? "border-[#1D4ED8]/25 bg-[#1D4ED8]/10 text-[#22D3EE]" : "border-[#2563EB]/25 bg-[#2563EB]/10 text-[#22D3EE]"}`}
                          >
                            {item.icon}
                          </div>

                          <div className="min-w-0">
                            <span
                              className={`inline-flex rounded-full border px-2 py-1 text-[10px] font-semibold uppercase tracking-[0.22em] ${isBusiness ? "border-[#1D4ED8]/20 bg-[#1D4ED8]/10 text-[#22D3EE]" : "border-[#2563EB]/20 bg-[#2563EB]/10 text-[#22D3EE]"}`}
                            >
                              {item.badge}
                            </span>
                            <p className="mt-3 text-sm font-semibold text-white/90">
                              {item.label}
                            </p>
                            <p className="mt-1 text-[11px] text-gray-500">
                              {item.caption}
                            </p>
                          </div>
                        </div>

                        {item.onClick && (
                          <span className="text-[10px] font-semibold uppercase tracking-[0.24em] text-[#2563EB]/55 transition-colors duration-300 group-hover/stat:text-[#22D3EE]">
                            Open
                          </span>
                        )}
                      </div>

                      <div className="relative mt-5 flex items-end justify-between gap-3">
                        <div>
                          <p
                            className={`text-2xl font-black tracking-tight ${isBusiness ? "text-[#1D4ED8]" : "text-[#2563EB]"}`}
                          >
                            {item.value}
                          </p>
                          <p className="mt-1 text-[10px] uppercase tracking-[0.28em] text-white/30">
                            {isBusiness ? "Business Volume" : "Active Count"}
                          </p>
                        </div>

                        <div
                          className={`rounded-full border px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] ${isBusiness ? "border-[#1D4ED8]/20 text-[#1D4ED8] bg-[#1D4ED8]/[0.08]" : "border-[#2563EB]/20 text-[#2563EB] bg-[#2563EB]/[0.08]"}`}
                        >
                          {isBusiness ? "USD" : "Members"}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>

              <div className="relative z-10 mt-4 rounded-2xl border border-[#2563EB]/10 bg-[#071526]/70 px-4 py-3 backdrop-blur-sm">
                <div className="flex items-center justify-between gap-3">
                  <p className="text-[11px] uppercase tracking-[0.26em] text-[#22D3EE]/70">
                    Quick Insight
                  </p>
                  <div className="h-px flex-1 bg-gradient-to-r from-[#2563EB]/20 to-transparent"></div>
                </div>
                <p className="mt-2 text-xs text-gray-400">
                  Use member cards to jump into your referral network. Business
                  cards show current direct and total team turnover.
                </p>
              </div>

              {/* Particles effect */}
              <div className="absolute top-10 left-10 w-1 h-1 bg-[#1D4ED8] rounded-full opacity-60 animate-ping"></div>
              <div
                className="absolute bottom-20 right-16 w-1 h-1 bg-[#22D3EE] rounded-full opacity-60 animate-ping"
                style={{ animationDelay: "1s" }}
              ></div>
              <div
                className="absolute top-1/2 left-20 w-1 h-1 bg-[#1D4ED8] rounded-full opacity-60 animate-ping"
                style={{ animationDelay: "2s" }}
              ></div>

              {/* Extra Particles (added) */}
              <div
                className="absolute top-6 right-8 w-1.5 h-1.5 bg-[#2563EB] rounded-full opacity-70 animate-ping"
                style={{ animationDelay: "0.4s" }}
              ></div>
              <div
                className="absolute bottom-6 left-16 w-2 h-2 bg-purple-400 rounded-full opacity-60 animate-pulse"
                style={{ animationDelay: "0.9s" }}
              ></div>
              <div
                className="absolute top-1/3 right-4 w-1 h-1 bg-[#22D3EE] rounded-full opacity-50 animate-ping"
                style={{ animationDelay: "1.6s" }}
              ></div>

              {/* Shine effect on hover */}
              <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-[#2563EB]/5 to-transparent -skew-x-12 translate-x-[-200%] group-hover:translate-x-[200%] transition-transform duration-1000"></div>
              </div>

              {/* Signature / watermark */}
              <div className="absolute bottom-3 right-3 text-xs text-white/20 italic tracking-wide rotate-[-6deg] pointer-events-none">
                <div className="flex items-center gap-2">
                  <svg
                    className="w-4 h-4 text-white/10"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"
                      stroke="currentColor"
                      strokeWidth="0.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  <span>Verified Team Stats</span>
                </div>
              </div>
            </div>
          </div>

          {/* ===== GRID ===== */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-y-6 gap-x-4">
            <Card
              title="Current Wallet"
              value={`$${getFloatingValue(user_profile?.tr03_inc_wallet)}`}
              color="cyan"
              path=""
              navigate={navigate}
            />
            <Card
              title="Referral Bonus"
              value={`$${getFloatingValue(dashboard?.referral_income)}`}
              color="red"
              path="/income/direct"
              navigate={navigate}
            />
            {/* <Card title="Total Capping" value="3X" color="green" /> */}
            {/* <Card title="Profit Ratio" value="$ 0" color="yellow" /> */}

            <Card
              title="Level Bonus"
              value={`$${getFloatingValue(dashboard?.level_bonus || 0)}`}
              color="cyan"
              path="/income/level"
              navigate={navigate}
            />
            <Card
              title="ROI Bonus"
              value={`$${getFloatingValue(dashboard?.roi_bonus || 0)}`}
              color="cyan"
              path="/income/roi"
              navigate={navigate}
            />
            <Card
              title="Reward Bonus"
              value={`$${getFloatingValue(dashboard?.reward_bonus || 0)}`}
              color="red"
              path="/income/reward"
              navigate={navigate}
            />
            <Card
              title="Salary Bonus"
              value={`$${getFloatingValue(dashboard?.salary_bonus || 0)}`}
              color="green"
              path="/income/salary"
              navigate={navigate}
            />
            <Card
              title="Total Income"
              value={`$${getFloatingValue(dashboard?.total_income || 0)}`}
              color="yellow"
              path=""
              navigate={navigate}
            />

            {/* <Card title="Total Withdrawal" value="$ 0" color="cyan" icon="user" /> */}
            {/* <Card
          title="Direct Member"
          value={user_profile?.tr03_dir_mem || 0}
          color="red"
        />
        <Card
          title="Team Member"
          value={user_profile?.tr03_team_mem || 0}
          color="green"
          icon="user"
        /> */}
            {/* <Card title="Downline Team" value="0" color="yellow" /> */}

            {/* <Card
          title="Downline Business"
          value={user_profile?.jnr_team_topup_bal || 0}
          color="cyan"
          icon="user"
        /> */}
          </div>
        </div>
        {/* /hidden old grid */}
      </div>

      {/* ===== Transfer Income to Fund Wallet Dialog ===== */}
      {transferDialogOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/70 backdrop-blur-sm"
            onClick={() => {
              setTransferDialogOpen(false);
              setTransferAmount("");
            }}
          ></div>

          {/* Dialog */}
          <div className="relative z-10 w-full max-w-sm mx-4 bg-gradient-to-br from-[#060E1C] via-[#080F1C] to-[#0B1E36] rounded-3xl border border-[#2563EB]/40 shadow-2xl shadow-[#2563EB]/25 overflow-hidden">
            <div className="absolute -inset-0.5 bg-gradient-to-r from-[#2563EB] to-[#1D4ED8] rounded-3xl opacity-20 blur-lg pointer-events-none"></div>
            {/* Accent line */}
            <div className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-[#22D3EE] via-[#2563EB] to-[#1D4ED8]"></div>

            {/* Decorative glow */}
            <div className="absolute top-0 right-0 w-40 h-40 bg-[#2563EB]/5 rounded-full blur-3xl pointer-events-none"></div>

            <div className="p-6">
              {/* Header */}
              <div className="flex items-center justify-between mb-5">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-[#2563EB] animate-pulse"></div>
                  <h3 className="text-base font-bold text-transparent bg-clip-text bg-gradient-to-r from-[#22D3EE] via-[#2563EB] to-[#1D4ED8] uppercase tracking-wide">
                    Transfer to Fund Wallet
                  </h3>
                </div>
                <button
                  onClick={() => {
                    setTransferDialogOpen(false);
                    setTransferAmount("");
                  }}
                  className="w-8 h-8 flex items-center justify-center rounded-full bg-[#071526]/80 border border-[#2563EB]/20 text-gray-400 hover:text-white hover:border-[#2563EB]/50 transition-all duration-200"
                >
                  ✕
                </button>
              </div>

              {/* Balance info */}
              <div className="mb-4 bg-[#071526]/30 rounded-lg py-2 px-3 border border-[#2563EB]/10">
                <p className="text-xs text-gray-400 text-center">
                  Available Income Wallet:{" "}
                  <span className="text-[#22D3EE] font-semibold">
                    {getFloatingValue(user_profile?.tr03_inc_wallet)} USD
                  </span>
                </p>
              </div>

              {/* Amount Input */}
              <div className="mb-5">
                <label className="block text-xs text-gray-400 font-medium uppercase tracking-wide mb-2">
                  Amount (USD)
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#2563EB] font-bold text-sm">
                    $
                  </span>
                  <input
                    type="number"
                    min="0"
                    value={transferAmount}
                    onChange={(e) => setTransferAmount(e.target.value)}
                    placeholder="Enter amount"
                    className="w-full pl-7 pr-4 py-2.5 rounded-lg bg-[#071526]/80 border border-[#2563EB]/30 text-white text-sm placeholder-gray-600 focus:outline-none focus:border-[#2563EB]/70 transition-colors duration-200"
                  />
                </div>
              </div>

              {/* Buttons */}
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setTransferDialogOpen(false);
                    setTransferAmount("");
                  }}
                  className="flex-1 py-2.5 rounded-full border border-[#2563EB]/30 text-gray-400 text-sm font-semibold hover:border-[#2563EB]/60 hover:text-white transition-all duration-200"
                >
                  Cancel
                </button>
                <button
                  onClick={handleTransferSubmit}
                  disabled={isTransferring}
                  className="flex-1 relative py-2.5 rounded-full bg-gradient-to-r from-[#1D4ED8] to-[#2563EB] hover:from-[#1db357] hover:to-[#00c8d4] text-white text-sm font-bold shadow-lg shadow-[#1D4ED8]/40 transition-all duration-300 hover:scale-105 disabled:opacity-60 disabled:cursor-not-allowed overflow-hidden"
                >
                  {isTransferring ? (
                    <span className="flex items-center justify-center gap-2">
                      <svg
                        className="animate-spin w-4 h-4"
                        viewBox="0 0 24 24"
                        fill="none"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 00-8 8h4z"
                        ></path>
                      </svg>
                      Processing...
                    </span>
                  ) : (
                    "Submit"
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Dashboard;

const NewsTicker = ({ items }) => {
  return (
    <div
      className="relative w-full overflow-hidden flex items-center my-2"
      style={{
        background:
          "linear-gradient(90deg, rgba(6,14,28,0.98) 0%, rgba(8,15,28,0.95) 100%)",
        border: "1px solid rgba(37,99,235,0.20)",
        borderRadius: "0.75rem",
        boxShadow:
          "0 4px 24px rgba(0,0,0,0.4), inset 0 1px 0 rgba(37,99,235,0.08)",
        height: "42px",
      }}
    >
      {/* Top shimmer */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#2563EB]/40 to-transparent pointer-events-none" />

      {/* LIVE badge */}
      <div
        className="flex-shrink-0 flex items-center gap-1.5 px-4 h-full z-10 relative"
        style={{
          background:
            "linear-gradient(90deg, rgba(37,99,235,0.18) 0%, rgba(29,78,216,0.08) 100%)",
          borderRight: "1px solid rgba(37,99,235,0.25)",
          minWidth: "72px",
        }}
      >
        <div className="w-1.5 h-1.5 rounded-full bg-[#2563EB] animate-pulse flex-shrink-0" />
        <span
          className="text-[10px] font-black tracking-widest uppercase"
          style={{ color: "rgba(0,180,190,0.9)" }}
        >
          LIVE
        </span>
      </div>

      {/* Left fade overlay */}
      <div
        className="absolute left-[72px] top-0 bottom-0 w-8 z-10 pointer-events-none"
        style={{
          background: "linear-gradient(90deg, rgba(8,15,28,0.95), transparent)",
        }}
      />

      {/* Scrolling ticker track */}
      <div className="flex-1 overflow-hidden relative h-full flex items-center">
        <div className="ticker-track flex items-center gap-0 whitespace-nowrap">
          {/* Duplicate items for seamless loop */}
          {[...items, ...items].map((item, i) => (
            <div key={i} className="flex items-center flex-shrink-0">
              <span
                className="text-sm font-medium px-2"
                style={{ color: "rgba(203,213,225,0.85)" }}
              >
                {item}
              </span>
              {/* Separator dot */}
              <span
                className="w-1 h-1 rounded-full flex-shrink-0 mx-3"
                style={{ background: "rgba(0,180,190,0.4)" }}
              />
            </div>
          ))}
        </div>
      </div>

      {/* Right fade overlay */}
      <div
        className="absolute right-0 top-0 bottom-0 w-12 pointer-events-none z-10"
        style={{
          background:
            "linear-gradient(270deg, rgba(6,14,28,0.98), transparent)",
        }}
      />

      {/* Bottom accent line */}
      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#2563EB]/20 to-transparent pointer-events-none" />

      <style>{`
        .ticker-track {
          animation: ticker-scroll 18s linear infinite;
          will-change: transform;
        }

        .ticker-track:hover {
          animation-play-state: paused;
        }

        @keyframes ticker-scroll {
          0%   { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
      `}</style>
    </div>
  );
};
